import { useEffect, useState } from 'react'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAccountingCustomerContext } from '@/api/accounting-dealer/customer/read-customer-context'
import { useCreditCustomerContext } from '@/api/financial/customer/read-customer-context'
import { useReadUserProfile } from '@/api/user-manager'
import {
  companyRouteFor,
  hasSeenAnnouncement,
  markAnnouncementSeen,
  readSelectedProduct,
  rememberSelectedCompany,
  STORAGE_COMPANY_KEY,
  writeSelectedProduct,
} from '@/helpers'
import { routes } from '@/router'
import { ProductSheet } from '@/components/product-switcher/product-sheet'
import { AccountingCustomerCompany } from '@/types/customer-context'
import { ProductContext } from './use-product'

export function ProductContextProvider() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { data: user } = useReadUserProfile()
  const accounting = useAccountingCustomerContext()
  const credit = useCreditCustomerContext()

  const stored = readSelectedProduct()
  const [selectedCompanyKey, setSelectedCompanyKey] = useState<string | null>(
    () =>
      stored?.product === 'accounting'
        ? stored.company_key
        : localStorage.getItem(STORAGE_COMPANY_KEY),
  )
  const [product, setProduct] = useState<'accounting' | 'credit'>(
    stored?.product === 'credit' ? 'credit' : 'accounting',
  )
  const [sheetOpen, setSheetOpen] = useState(false)
  const [welcome, setWelcome] = useState(false)
  const [tipOpen, setTipOpen] = useState(false)

  const companies = accounting.data?.companies ?? []
  const applications = credit.data?.applications ?? []
  const hasAccounting = accounting.isSuccess && companies.length > 0
  const hasCredit = credit.isSuccess && applications.length > 0
  const accountingAvailable = accounting.isSuccess
  const creditAvailable = true
  const profileName = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .join(' ')
  const creditHasPending = applications.some(
    (application) => application.has_pending,
  )
  const userKey = user?.key
  const hiring =
    matchPath(
      { path: routes.accounting.hiring.index, end: false },
      pathname,
    ) !== null

  useEffect(() => {
    if (pathname.startsWith(routes.credit.index)) {
      setProduct('credit')
      writeSelectedProduct({ product: 'credit' })
      return
    }

    if (!pathname.startsWith(routes.accounting.index)) return

    setProduct('accounting')
    const companyKey =
      selectedCompanyKey || localStorage.getItem(STORAGE_COMPANY_KEY)
    if (companyKey) {
      writeSelectedProduct({
        product: 'accounting',
        company_key: companyKey,
      })
    }
  }, [pathname, selectedCompanyKey])
  const otherAvailable =
    product === 'credit' ? accountingAvailable : creditAvailable

  useEffect(() => {
    if (accounting.isLoading || credit.isLoading || !userKey || hiring) return

    const needsFirstLoginPicker =
      hasAccounting &&
      hasCredit &&
      !hasSeenAnnouncement('product-picker-first-login', userKey)

    if (needsFirstLoginPicker) {
      setWelcome(true)
      setSheetOpen(true)
      return
    }

    if (
      !welcome &&
      !sheetOpen &&
      otherAvailable &&
      !hasSeenAnnouncement('product-switcher-tip', userKey)
    ) {
      setTipOpen(true)
    }
  }, [
    accounting.isLoading,
    credit.isLoading,
    userKey,
    welcome,
    sheetOpen,
    otherAvailable,
    hasAccounting,
    hasCredit,
    hiring,
  ])

  const markWelcomeSeen = () => {
    if (!userKey) return
    markAnnouncementSeen('product-picker-first-login', userKey)
    setWelcome(false)
  }

  const dismissTip = () => {
    if (userKey) markAnnouncementSeen('product-switcher-tip', userKey)
    setTipOpen(false)
  }

  const openSheet = () => {
    dismissTip()
    setSheetOpen(true)
  }

  const closeSheet = () => {
    if (welcome) markWelcomeSeen()
    setSheetOpen(false)
  }

  const selectCredit = () => {
    writeSelectedProduct({ product: 'credit' })
    setProduct('credit')
    markWelcomeSeen()
    dismissTip()
    setSheetOpen(false)
    navigate(routes.credit.index)
  }

  const selectCompany = (company: AccountingCustomerCompany) => {
    rememberSelectedCompany(company)
    if (company.company_key) {
      setSelectedCompanyKey(company.company_key)
    }
    setProduct('accounting')
    markWelcomeSeen()
    dismissTip()
    setSheetOpen(false)
    navigate(companyRouteFor(company))
  }

  return (
    <ProductContext.Provider
      value={{
        product,
        companies,
        personName: credit.data?.person?.name || profileName,
        personTaxId: credit.data?.person?.tax_id ?? null,
        firstName: user?.first_name ?? '',
        hasAccounting,
        hasCredit,
        accountingLoading: accounting.isLoading,
        accountingAvailable,
        creditAvailable,
        creditHasPending,
        selectedCompanyKey,
        sheetOpen,
        welcome,
        tipOpen,
        openSheet,
        closeSheet,
        dismissTip,
        selectCredit,
        selectCompany,
      }}
    >
      <Outlet />
      <ProductSheet />
    </ProductContext.Provider>
  )
}
